import React, { useState, useCallback, useEffect, useMemo } from "react";
import * as XLSX from "xlsx";
import {
    Search, Loader2, AlertCircle, Copy, Check, FileType,
    ChevronLeft, ChevronRight, Trash2, X, CheckCircle2, Download,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogTitle, DialogClose } from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
    AlertDialogTitle, AlertDialogDescription,
} from "@/components/ui/alert-dialog";
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { environment } from "@/env/env.prod";
import { apiHeaders } from "@/Auth/api-headers.";

// ─────────────────────────────────────────────────────────────────────────
// Classroom roster — who is registered in this classroom, read from
// classrooms/getClassRoomRoster. The whole roster is loaded once (it was
// already being fetched in full for the "already added" check and for
// export), so filtering and paging happen in the browser.
//
// Removing a student uses the registery endpoints that already exist:
//   classrooms/unregisterStudent        one student (soft delete)
//   classrooms/clearClassroomRegistery  everyone, in one call
// Several students are removed with one unregisterStudent call each; there is
// no bulk endpoint. Both need an admin / subadmin / superadmin on the server,
// so a faculty member without that role sees the server's refusal.
//
//
// Adding students is not done here: enrolment lives in the Registery dialog
// (add one, bulk upload, clear). This list is read from that same registry.
// ─────────────────────────────────────────────────────────────────────────

type RosterMember = {
    uid: string;
    name: string;
    eid: string;
    email: string;
    profilePic: string | null;
    // false until the student has signed in to the app at least once
    linked: boolean;
    joinedAt: number | null;
};

type RemoveFailure = { eid: string; name: string; message: string };
type RemoveReport = { removed: number; failed: RemoveFailure[]; notAttempted: number };

type Props = {
    open: boolean;
    onClose: () => void;
    classRoomId: string | null;
    classroomName: string;
    coordinatorName?: string;
};

const PAGE_SIZE = 25;
// Parallel unregisterStudent calls while removing several students.
const REMOVE_CONCURRENCY = 4;

function initialsFor(name: string) {
    return (name || "?").split(" ").map(n => n[ 0 ]).join("").substring(0, 2).toUpperCase();
}

function formatJoinedAt(ts: number | null) {
    if (!ts) return "—";
    return new Date(ts).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

// POST to a classrooms/* route; throws with the server's message and the
// HTTP status so a 403 can be told apart from an ordinary failure.
async function postClassrooms(path: string, body: object) {
    const res = await fetch(`${environment.BASE_URL}/classrooms/${path}`, {
        method: "POST",
        headers: apiHeaders(),
        body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.success) {
        const err: Error & { status?: number } = new Error(data.message || `Request failed (${res.status})`);
        err.status = res.status;
        throw err;
    }
    return data;
}

export function ClassroomRoster({ open, onClose, classRoomId, classroomName, coordinatorName }: Props) {
    const [ members, setMembers ] = useState<RosterMember[]>([]);
    const [ loading, setLoading ] = useState(false);
    const [ error, setError ] = useState<string | null>(null);

    const [ filter, setFilter ] = useState("");
    const [ page, setPage ] = useState(1);
    // E-IDs of the rows ticked for removal. E-ID, not uid: a student who has
    // never signed in has no uid of their own (the roster reuses the E-ID).
    const [ selected, setSelected ] = useState<Set<string>>(new Set());

    const [ copySuccess, setCopySuccess ] = useState(false);
    const [ exporting, setExporting ] = useState(false);

    const [ removeTarget, setRemoveTarget ] = useState<RosterMember[] | null>(null);
    const [ removing, setRemoving ] = useState<{ done: number; total: number } | null>(null);
    const [ removeReport, setRemoveReport ] = useState<RemoveReport | null>(null);

    // Loads every page of the roster. Used for the list itself, the "already
    // added" check, copy-emails and export — a partial list would be
    // misleading for the last two.
    const loadAll = useCallback(async () => {
        if (!classRoomId) return;
        setLoading(true);
        setError(null);
        try {
            const all: RosterMember[] = [];
            for (let p = 1; ; p++) {
                const params = new URLSearchParams({ classRoomId, page: String(p), limit: "100" });
                const res = await fetch(`${environment.BASE_URL}/classrooms/getClassRoomRoster?${params}`, { headers: apiHeaders() });
                const data = await res.json();
                if (!data.success) throw new Error(data.message || "Failed to load roster");
                all.push(...(data.members || []));
                if (!data.hasMore) break;
            }
            setMembers(all);
        } catch (e: any) {
            setError(e.message || "Failed to load roster");
        } finally {
            setLoading(false);
        }
    }, [ classRoomId ]);

    useEffect(() => {
        if (!open || !classRoomId) return;
        setFilter("");
        setPage(1);
        setSelected(new Set());
        setRemoveReport(null);
        loadAll();
    }, [ open, classRoomId, loadAll ]);

    // ── filter + page ────────────────────────────────────────────────────
    const filtered = useMemo(() => {
        const q = filter.trim().toLowerCase();
        if (!q) return members;
        return members.filter(m => [ m.name, m.eid, m.email ].some(v => (v || "").toLowerCase().includes(q)));
    }, [ members, filter ]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const currentPage = Math.min(page, totalPages);
    const pageRows = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    // ── selection ────────────────────────────────────────────────────────
    const selectedCount = filtered.filter(m => selected.has(m.eid)).length;
    const allFilteredSelected = filtered.length > 0 && selectedCount === filtered.length;
    const headerChecked: boolean | "indeterminate" = allFilteredSelected ? true : selectedCount > 0 ? "indeterminate" : false;

    const toggleOne = (eid: string) => setSelected(prev => {
        const next = new Set(prev);
        if (next.has(eid)) next.delete(eid); else next.add(eid);
        return next;
    });

    // Acts on every row the filter matches, not just the visible page.
    const toggleAllFiltered = () => setSelected(prev => {
        const next = new Set(prev);
        if (allFilteredSelected) filtered.forEach(m => next.delete(m.eid));
        else filtered.forEach(m => next.add(m.eid));
        return next;
    });

    // ── remove ───────────────────────────────────────────────────────────
    const requestRemoveSelected = () => setRemoveTarget(members.filter(m => selected.has(m.eid)));

    const confirmRemove = async () => {
        if (!classRoomId || !removeTarget || removeTarget.length === 0) return;
        const targets = removeTarget;
        const failed: RemoveFailure[] = [];
        let removed = 0;
        let notAttempted = 0;
        setRemoving({ done: 0, total: targets.length });

        try {
            if (targets.length > 1 && targets.length === members.length) {
                // Everyone — one call, not one per student.
                await postClassrooms("clearClassroomRegistery", { classRoomId });
                removed = targets.length;
            } else {
                let next = 0;
                let stop = false;
                const worker = async () => {
                    while (!stop) {
                        const i = next++;
                        if (i >= targets.length) return;
                        const m = targets[ i ];
                        try {
                            await postClassrooms("unregisterStudent", { classRoomId, studentEid: m.eid });
                            removed++;
                        } catch (e: any) {
                            failed.push({ eid: m.eid, name: m.name || m.eid, message: e.message });
                            // Not permitted — every remaining call would fail the same way.
                            if (e.status === 403) stop = true;
                        }
                        setRemoving({ done: removed + failed.length, total: targets.length });
                    }
                };
                await Promise.all(Array.from({ length: Math.min(REMOVE_CONCURRENCY, targets.length) }, worker));
                notAttempted = targets.length - removed - failed.length;
            }
        } catch (e: any) {
            failed.push({ eid: "", name: "All students", message: e.message });
        }

        setRemoving(null);
        setRemoveTarget(null);
        setRemoveReport({ removed, failed, notAttempted });
        // Leave the ones that did not go ticked so they can be retried.
        setSelected(new Set(failed.map(f => f.eid).filter(Boolean)));
        await loadAll();
    };

    // ── tools ────────────────────────────────────────────────────────────
    const handleCopyEmails = () => {
        navigator.clipboard.writeText(members.map(m => m.email).filter(Boolean).join(", "));
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
    };

    const handleDownload = (format: "csv" | "xlsx") => {
        setExporting(true);
        try {
            const exportData = members.map(m => ({
                "Student Name": m.name,
                "E-ID": m.eid,
                "Email": m.email,
                "Joined On": formatJoinedAt(m.joinedAt),
            }));
            const fileName = `Roster_${classroomName}`.replace(/\s+/g, "_");
            if (exportData.length === 0) return;
            if (format === "csv") {
                const headers = Object.keys(exportData[ 0 ]).join(",");
                const rows = exportData.map(r => Object.values(r).map(v => `"${v}"`).join(",")).join("\n");
                const blob = new Blob([ `${headers}\n${rows}` ], { type: "text/csv" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url; a.download = `${fileName}.csv`; a.click();
            } else {
                const ws = XLSX.utils.json_to_sheet(exportData);
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, "Roster");
                XLSX.writeFile(wb, `${fileName}.xlsx`);
            }
        } finally {
            setExporting(false);
        }
    };

    // "Everyone" only makes sense for more than one student; a lone student is
    // worded and removed like any single removal.
    const removingAll = !!removeTarget && removeTarget.length > 1 && removeTarget.length === members.length;
    const firstRow = filtered.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
    const lastRow = Math.min(currentPage * PAGE_SIZE, filtered.length);

    return (
        <>
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            {/* sm:max-w-4xl, not max-w-4xl — the dialog's own sm:max-w-lg would win and clamp it to 512px */}
            <DialogContent className="sm:max-w-4xl h-[88vh] flex flex-col p-0 overflow-hidden border-border/60 shadow-2xl">
                {/* ── Header: title, find, add, tools ───────────────────── */}
                <div className="px-6 pt-5 pb-4 border-b border-border/40 bg-muted/5 shrink-0">
                    {/* pr-8 keeps the coordinator badge clear of the dialog's close button */}
                    <div className="flex justify-between items-start gap-3 pr-8">
                        <div className="min-w-0">
                            <DialogTitle className="text-xl font-bold tracking-tight">Classroom Roster</DialogTitle>
                            <p className="text-xs text-muted-foreground mt-0.5 truncate">
                                {classroomName} · {members.length} student{members.length === 1 ? "" : "s"}
                                {members.length > 0 && ` · ${members.filter(m => !m.linked).length} not signed up yet`}
                            </p>
                        </div>
                        {coordinatorName && (
                            <Badge className="bg-primary/10 text-primary border-primary/20 shrink-0">{coordinatorName}</Badge>
                        )}
                    </div>

                    <div className="mt-4 flex flex-wrap items-center gap-2">
                        <div className="relative flex-1 min-w-[220px]">
                            <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                placeholder="Filter by name, E-ID or email…"
                                value={filter}
                                onChange={(e) => { setFilter(e.target.value); setPage(1); }}
                                className="h-9 text-xs pl-8 pr-8"
                            />
                            {filter && (
                                <button
                                    type="button"
                                    aria-label="Clear filter"
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                    onClick={() => { setFilter(""); setPage(1); }}
                                >
                                    <X className="h-3.5 w-3.5" />
                                </button>
                            )}
                        </div>
                        <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={handleCopyEmails} disabled={members.length === 0}>
                            {copySuccess ? <><Check className="h-3.5 w-3.5 text-emerald-500" /> Copied</> : <><Copy className="h-3.5 w-3.5" /> Copy emails</>}
                        </Button>
                        <Select onValueChange={(v: "csv" | "xlsx") => handleDownload(v)}>
                            <SelectTrigger className="h-9 w-[130px] text-xs gap-1.5" disabled={members.length === 0}>
                                <span className="flex items-center gap-1.5"><Download className="h-3.5 w-3.5" />{exporting ? "Exporting…" : "Download"}</span>
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="csv"><div className="flex items-center gap-2 font-bold"><FileType className="h-3.5 w-3.5" /> CSV</div></SelectItem>
                                <SelectItem value="xlsx"><div className="flex items-center gap-2 font-bold"><FileType className="h-3.5 w-3.5" /> Excel</div></SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {/* What the last removal did */}
                    {removeReport && (
                        <div className={`mt-3 rounded-lg border p-3 text-xs ${removeReport.failed.length > 0 ? "border-amber-300 bg-amber-50" : "border-emerald-200 bg-emerald-50"}`}>
                            <div className="flex items-center gap-2">
                                {removeReport.failed.length > 0
                                    ? <AlertCircle className="h-3.5 w-3.5 text-amber-600" />
                                    : <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />}
                                <span className="font-semibold">
                                    {removeReport.removed} removed
                                    {removeReport.failed.length > 0 && ` · ${removeReport.failed.length} failed`}
                                    {removeReport.notAttempted > 0 && ` · ${removeReport.notAttempted} not attempted`}
                                </span>
                                <Button variant="ghost" size="sm" className="ml-auto h-6 w-6 p-0" onClick={() => setRemoveReport(null)}>
                                    <X className="h-3 w-3" />
                                </Button>
                            </div>
                            {removeReport.failed.length > 0 && (
                                <ul className="mt-1.5 max-h-[90px] overflow-y-auto space-y-0.5 text-[11px]">
                                    {removeReport.failed.map((f, i) => (
                                        <li key={`${f.eid}-${i}`}><span className="font-medium">{f.name}</span> — {f.message}</li>
                                    ))}
                                </ul>
                            )}
                            {removeReport.failed.length > 0 && (
                                <p className="mt-1 text-[11px] text-muted-foreground">The ones that failed are still ticked, so you can try again.</p>
                            )}
                        </div>
                    )}
                </div>

                {/* ── Selection bar ─────────────────────────────────────── */}
                {selected.size > 0 && (
                    <div className="px-6 py-2 flex items-center gap-3 bg-primary/5 border-b border-primary/20 shrink-0">
                        <span className="text-xs font-semibold">{selected.size} selected</span>
                        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setSelected(new Set())}>
                            Clear selection
                        </Button>
                        <Button
                            variant="destructive"
                            size="sm"
                            className="h-8 gap-1.5 ml-auto"
                            onClick={requestRemoveSelected}
                            disabled={!!removing}
                        >
                            <Trash2 className="h-3.5 w-3.5" /> Remove {selected.size}
                        </Button>
                    </div>
                )}

                {/* ── List ──────────────────────────────────────────────── */}
                <div className="flex-1 overflow-y-auto bg-[oklch(0.99_0.005_90)]">
                    {loading ? (
                        <div className="flex items-center justify-center h-40"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
                    ) : error ? (
                        <div className="flex flex-col items-center justify-center h-40 gap-2">
                            <AlertCircle className="h-6 w-6 text-red-400" />
                            <p className="text-sm text-muted-foreground">{error}</p>
                            <Button variant="outline" size="sm" onClick={loadAll}>Try again</Button>
                        </div>
                    ) : members.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-12">
                            No students in this classroom yet. Add them from Registery.
                        </p>
                    ) : filtered.length === 0 ? (
                        <div className="text-center py-12">
                            <p className="text-sm text-muted-foreground">No student matches “{filter}”.</p>
                            <Button variant="ghost" size="sm" className="mt-2 text-xs" onClick={() => { setFilter(""); setPage(1); }}>Clear filter</Button>
                        </div>
                    ) : (
                        <Table>
                            <TableHeader className="sticky top-0 bg-card z-10">
                                <TableRow>
                                    <TableHead className="w-10 pl-6">
                                        <Checkbox
                                            checked={headerChecked}
                                            onCheckedChange={toggleAllFiltered}
                                            aria-label={`Select all ${filtered.length} matching students`}
                                            title={`Select all ${filtered.length}${filter ? " matching" : ""}`}
                                        />
                                    </TableHead>
                                    <TableHead>Student</TableHead>
                                    <TableHead>Email</TableHead>
                                    <TableHead className="whitespace-nowrap">Added</TableHead>
                                    <TableHead>App</TableHead>
                                    <TableHead className="w-12 pr-6" />
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {pageRows.map(m => {
                                    const isSelected = selected.has(m.eid);
                                    return (
                                        <TableRow key={m.eid} data-state={isSelected ? "selected" : undefined} className={isSelected ? "bg-primary/5" : undefined}>
                                            <TableCell className="pl-6">
                                                <Checkbox
                                                    checked={isSelected}
                                                    onCheckedChange={() => toggleOne(m.eid)}
                                                    aria-label={`Select ${m.name || m.eid}`}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-3 min-w-0">
                                                    <Avatar className="h-8 w-8 shrink-0 border border-border/40">
                                                        <AvatarImage src={m.profilePic ?? ""} />
                                                        <AvatarFallback className="bg-primary/5 text-primary/70 text-[11px] font-bold">{initialsFor(m.name)}</AvatarFallback>
                                                    </Avatar>
                                                    <div className="min-w-0">
                                                        <p className="text-sm font-semibold truncate">{m.name || "—"}</p>
                                                        <p className="text-[11px] text-muted-foreground font-mono">{m.eid || "—"}</p>
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-xs text-muted-foreground max-w-[220px] truncate">{m.email || "—"}</TableCell>
                                            <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{formatJoinedAt(m.joinedAt)}</TableCell>
                                            <TableCell>
                                                {m.linked ? (
                                                    <Badge variant="outline" className="text-[10px] text-emerald-700 bg-emerald-50 border-emerald-200">Signed up</Badge>
                                                ) : (
                                                    <Badge variant="outline" className="text-[10px] text-muted-foreground">Not yet</Badge>
                                                )}
                                            </TableCell>
                                            <TableCell className="pr-6 text-right">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                                                    aria-label={`Remove ${m.name || m.eid}`}
                                                    title="Remove from this classroom"
                                                    onClick={() => setRemoveTarget([ m ])}
                                                    disabled={!!removing}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    )}
                </div>

                <div className="px-6 py-3 border-t border-border/40 flex items-center justify-between bg-card shrink-0">
                    <p className="text-xs text-muted-foreground">
                        {filtered.length === 0
                            ? "No students"
                            : `Showing ${firstRow}–${lastRow} of ${filtered.length}${filter ? " matching" : ""}`}
                    </p>
                    <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground mr-1">Page {currentPage} of {totalPages}</span>
                        <Button variant="outline" size="sm" className="h-8 text-xs gap-1" disabled={currentPage <= 1 || loading} onClick={() => setPage(currentPage - 1)}>
                            <ChevronLeft className="h-3.5 w-3.5" /> Prev
                        </Button>
                        <Button variant="outline" size="sm" className="h-8 text-xs gap-1" disabled={currentPage >= totalPages || loading} onClick={() => setPage(currentPage + 1)}>
                            Next <ChevronRight className="h-3.5 w-3.5" />
                        </Button>
                        <DialogClose asChild>
                            <Button variant="ghost" className="text-xs font-bold px-5 ml-2">Close</Button>
                        </DialogClose>
                    </div>
                </div>
            </DialogContent>
        </Dialog>

        {/* ── REMOVE CONFIRMATION ───────────────────────────────────────── */}
        <AlertDialog open={removeTarget !== null} onOpenChange={(next) => { if (!next && !removing) setRemoveTarget(null); }}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>
                        {removingAll
                            ? `Remove all ${removeTarget?.length} students?`
                            : `Remove ${removeTarget?.length} student${removeTarget?.length === 1 ? "" : "s"}?`}
                    </AlertDialogTitle>
                    <AlertDialogDescription asChild>
                        <div className="space-y-2">
                            <p>
                                {removingAll ? "Everyone" : removeTarget?.length === 1 ? "This student" : "These students"} will be
                                unregistered from {classroomName || "this classroom"}, which frees them to be registered in another
                                classroom. Attendance already recorded is not affected — it stays exactly as it was taken.
                            </p>
                            {!removingAll && removeTarget && (
                                <ul className="text-xs list-disc pl-5 space-y-0.5 text-foreground">
                                    {removeTarget.slice(0, 6).map(m => (
                                        <li key={m.eid}>{m.name || "—"} <span className="font-mono text-muted-foreground">({m.eid})</span></li>
                                    ))}
                                    {removeTarget.length > 6 && <li className="list-none -ml-5 text-muted-foreground">and {removeTarget.length - 6} more</li>}
                                </ul>
                            )}
                        </div>
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <Button variant="outline" onClick={() => setRemoveTarget(null)} disabled={!!removing}>Cancel</Button>
                    <Button variant="destructive" onClick={confirmRemove} disabled={!!removing}>
                        {removing
                            ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> Removing {removing.done} of {removing.total}…</>
                            : "Remove"}
                    </Button>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>

        </>
    );
}
